import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  ClientMetaKey,
  DueDateMetaKey,
  ExpenditureTypeMetaKey,
  InvoiceDateMetaKey,
  InvoicedSinceMetaKey,
  ProjectMetaKey,
  TemplateMetaKey,
  TitleMetaKey,
  TmGroupingMetaKey,
  TmHoursMetaKey,
  TmHoursSinceMetaKey,
} from '../../invoice.config';
import { InvoiceTimespan } from '../../invoice-types';
import { InvoiceType } from '../../../../../core/constants-repository/project-calculation.constants';
import { Observable, of } from 'rxjs';
import { GroupResult } from '@progress/kendo-data-query';
import {
  DropdownDefaultItem,
  DropdownOption,
  GroupedDropdownDefaultItem,
} from '../../../../../shared/data-types/dropdown-option';
import { assert } from '../../../../../core/assert/assert';
import { DateTimeHelper } from '../../../../../features/calendar/util/date-time-helper';
import { map, switchMap, take } from 'rxjs/operators';
import { faCirclePlus } from '@fortawesome/pro-regular-svg-icons';
import { InvoicePositionHandlerService } from '../../invoice-creation-editing/invoice-position-handler.service';
import { InvoiceStateService } from '../../invoice-creation-editing/state-and-data-handling/invoice-state.service';
import { GroupingOptions } from '../../grouping-constants';
import { UserOperation } from '../../../../../core/enums/user-operation';
import { ShiveDialogService } from '../../../../../core/services/controls/shive-dialog.service';
import { CreateInvoiceDialogService } from './create-invoice-dialog.service';
import { ProjectModel } from '../../../../../core/models/project/project.model';
import { InvoiceDocumentHttpService } from '../../../../../core/state/invoice-documents/invoice-document-http.service';
import {
  addDefaultOption,
  entityToDropdownOption,
} from '../../../../../shared/functions/dropdown-functions';
import { findKvRecordValue } from '../../../../../shared/functions/record-functions';
import { SubSink } from 'subsink';
import { Store } from '@ngrx/store';
import { selectInvoiceDocumentById } from '../../../../../core/state/invoice-documents/invoice-document.selectors';
import { FormControlNameRecord } from '../../../../../shared/data-types/form-types';

@Component({
  selector: 'app-create-invoice-dialog',
  templateUrl: './create-invoice-dialog.component.html',
  standalone: false,
})
export class CreateInvoiceDialogComponent implements OnInit, OnDestroy {
  // Observables
  public projects$: Observable<GroupResult[]> = of([GroupedDropdownDefaultItem]);

  // Forms
  public formControlNames: FormControlNameRecord = {
    Template: TemplateMetaKey,
    Title: TitleMetaKey,
    Client: ClientMetaKey,
    Project: ProjectMetaKey,
    InvoiceDate: InvoiceDateMetaKey,
    DueDate: DueDateMetaKey,
    TmHours: TmHoursMetaKey,
    TmGrouping: TmGroupingMetaKey,
    ExpenditureType: ExpenditureTypeMetaKey,
    TmHoursSince: TmHoursSinceMetaKey,
  };
  public invoiceDataForm: FormGroup;

  // Dropdowns / constants
  public expenditureTypeOptions: DropdownOption[] = [DropdownDefaultItem];
  public readonly GroupingOptions = GroupingOptions;
  public readonly InvoiceTimespan = InvoiceTimespan;
  public selectedTemplateOption: DropdownOption = null;
  public templates: DropdownOption[];

  // Constants
  public readonly InvoiceType = InvoiceType;
  public readonly faCirclePlus = faCirclePlus;
  public readonly UserOperation = UserOperation;

  // DI
  private readonly formBuilder = inject(FormBuilder);
  private readonly createInvoiceDialogService = inject(CreateInvoiceDialogService);
  private readonly invoiceStateService = inject(InvoiceStateService);
  private readonly invoicePositionHandlerService = inject(InvoicePositionHandlerService);
  private readonly invoiceDocumentHttpService = inject(InvoiceDocumentHttpService);
  private readonly shiveDialogService = inject(ShiveDialogService);
  private readonly store = inject(Store);

  private readonly subs = new SubSink();

  ngOnInit(): void {
    this.subs.sink = this.invoiceDocumentHttpService
      .fetchAllInvoiceDocuments(true)
      .pipe(
        map((docs) => [
          ...docs.map((doc) =>
            entityToDropdownOption({ id: doc.id, name: findKvRecordValue(doc.meta, TitleMetaKey) }),
          ),
        ]),
        addDefaultOption(),
      )
      .subscribe((templates) => {
        this.templates = templates;
        this.selectedTemplateOption = templates.length > 0 ? templates[0] : null;
      });

    this.bootstrap();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  public projectDropdownOpened(): void {
    this.projects$ = this.createInvoiceDialogService.loadGroupedProjects() as Observable<
      GroupResult[]
    >;
  }

  public async generate(): Promise<void> {
    const projectId = this.invoiceDataForm.get(this.formControlNames.Project)?.value?.value;
    if (projectId === null) {
      return;
    }

    const project = await this.createInvoiceDialogService.getProject(+projectId);
    if (!project) {
      return;
    }

    const expenditureType = this.invoiceDataForm.get(this.formControlNames.ExpenditureType)?.value
      ?.value;
    const tmHours = this.invoiceDataForm.get(this.formControlNames.TmHours)?.value;
    const tmGrouping = this.invoiceDataForm.get(this.formControlNames.TmGrouping)?.value;
    const invoicedSince = DateTimeHelper.startOfDay(
      this.invoiceDataForm.get(this.formControlNames.TmHoursSince)?.value,
    );
    const invoiceDate = DateTimeHelper.startOfDay(
      this.invoiceDataForm.get(this.formControlNames.InvoiceDate)?.value,
    );

    const dueDate = DateTimeHelper.startOfDay(
      this.invoiceDataForm.get(this.formControlNames.DueDate)?.value,
    );

    const config = {
      project: await this.createInvoiceDialogService.getProject(projectId),
      expenditureType,
      tmHours,
      tmGrouping,
      invoicedSince,
    };

    // Set invoice positions
    this.invoicePositionHandlerService
      .getInvoicePositions(config)
      .pipe(take(1))
      .subscribe((invoicePositions) => {
        this.invoiceStateService.invoicePositions.set(invoicePositions);

        // Update meta.
        this.updateMeta(
          project,
          expenditureType,
          tmHours,
          tmGrouping,
          invoicedSince,
          invoiceDate,
          dueDate,
        );

        // Close dialog and forward to invoice preview.
        this.shiveDialogService.close(UserOperation.Create);
      });
  }

  public close(): void {
    this.shiveDialogService.close(UserOperation.Cancel);
  }

  public async projectChanged(selectedProject: DropdownOption): Promise<void> {
    const projectId = +selectedProject?.value;
    assert(!isNaN(projectId));

    const project = await this.createInvoiceDialogService.getProject(projectId);
    if (!project) {
      // keep the dropdown defaulted
      this.resetExpenditureType();
      return;
    }

    // Derive options based on project
    this.expenditureTypeOptions = [DropdownDefaultItem];

    const expenditureOptions = this.createInvoiceDialogService.deriveExpenditureOptions(project);
    let selectedExpenditure = null;
    if (expenditureOptions.length > 0) {
      this.expenditureTypeOptions = expenditureOptions;
      selectedExpenditure = expenditureOptions[0];
    }
    this.resetExpenditureType();

    if (!selectedExpenditure) {
      return;
    }
  }

  public removeInvoiceTemplate($event: DropdownOption): void {
    this.subs.sink = this.store
      .select(selectInvoiceDocumentById(+$event.value))
      .pipe(
        take(1),
        switchMap((invoice) => this.invoiceDocumentHttpService.deleteInvoiceDocument(invoice)),
      )
      .subscribe();
  }

  private resetExpenditureType(): void {
    this.invoiceDataForm.controls[this.formControlNames.ExpenditureType].setValue(
      this.expenditureTypeOptions[0],
    );
  }

  private bootstrap(): void {
    const project = null;
    const invoiceDate = new Date();
    const dueDate = new Date();
    const expenditureType = null;
    const tmHours = InvoiceTimespan.TmHours;
    const tmGrouping = GroupingOptions.ByTask;
    const tmHoursSince = new Date();

    // Build invoice data form
    this.invoiceDataForm = this.formBuilder.group({
      [this.formControlNames.Template]: [null, Validators.required],
      [this.formControlNames.Title]: ['', Validators.required],
      [this.formControlNames.Project]: [project],
      [this.formControlNames.InvoiceDate]: [invoiceDate],
      [this.formControlNames.DueDate]: [dueDate],
      [this.formControlNames.ExpenditureType]: [expenditureType, Validators.required],
      [this.formControlNames.TmHours]: [tmHours],
      [this.formControlNames.TmGrouping]: [tmGrouping],
      [this.formControlNames.TmHoursSince]: [tmHoursSince],
    });
  }

  private updateMeta(
    project: ProjectModel,
    expenditureType: string,
    tmHours: string,
    tmGrouping: string,
    invoicedSince: Date,
    invoiceDate: Date,
    dueDate: Date,
  ): void {
    // Title
    const title = this.invoiceDataForm.get(this.formControlNames.Title).value;
    const recordTitle: FormControlNameRecord = { [TitleMetaKey]: title };
    this.invoiceStateService.updateMeta(recordTitle);

    // Project
    const recordProject: FormControlNameRecord = {
      [ProjectMetaKey]: JSON.stringify(project.name),
    };
    this.invoiceStateService.updateMeta(recordProject);

    // Expenditure
    const recordExpenditure: FormControlNameRecord = {
      [ExpenditureTypeMetaKey]: expenditureType,
    };
    this.invoiceStateService.updateMeta(recordExpenditure);

    // TmHours
    const recordTmHours: FormControlNameRecord = {
      [TmHoursMetaKey]: tmHours,
    };
    this.invoiceStateService.updateMeta(recordTmHours);

    // TmGrouping
    const recordTmGrouping: FormControlNameRecord = {
      [TmGroupingMetaKey]: tmGrouping,
    };
    this.invoiceStateService.updateMeta(recordTmGrouping);

    // invoiceDate
    const recordInvoiceDate: FormControlNameRecord = {
      [InvoiceDateMetaKey]: DateTimeHelper.dateToISO8601String(invoiceDate),
    };
    this.invoiceStateService.updateMeta(recordInvoiceDate);

    // invoicedSince
    const recordInvoicedSince: FormControlNameRecord = {
      [InvoicedSinceMetaKey]: DateTimeHelper.dateToISO8601String(invoicedSince),
    };
    this.invoiceStateService.updateMeta(recordInvoicedSince);

    // dueDate
    const recordDueDate: FormControlNameRecord = {
      [DueDateMetaKey]: DateTimeHelper.dateToISO8601String(dueDate),
    };
    this.invoiceStateService.updateMeta(recordDueDate);
  }
}
